import React, { useEffect } from "react";
import sal from "sal.js";

import teamMembers from "../../data/teamMembers";

const CATEGORY_ORDER = ["top-management", "architect-engineer", "management"];

const CATEGORY_META = {
  "top-management": {
    label: "Leadership",
    note: "Setting the direction and standards for every project we take on.",
  },
  "architect-engineer": {
    label: "Architecture & Engineering",
    note: "Turning drawings into structures, on site and at the desk.",
  },
  management: {
    label: "Management & Sales",
    note: "Keeping projects, clients, and accounts moving.",
  },
};

// "Md"/"Md." is a near-universal prefix in this roster, so we skip it
// when building initials — otherwise every monogram would start with M.

const getInitials = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const Monogram = ({ name, image, size = "text-4xl" }) => (
  <div className="flex aspect-square w-full items-center justify-center overflow-hidden bg-stone-100">
    {image ? (
      <img src={image} alt={name} className="h-full w-full object-cover" />
    ) : (
      <span className={`font-poiret ${size} text-secondary`}>
        {getInitials(name)}
      </span>
    )}
  </div>
);

const Teams = () => {
  useEffect(() => {
    sal({
      threshold: 1,
      once: true,
    });
  }, []);

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    members: teamMembers.filter((member) => member.category === category),
  })).filter((group) => group.members.length);

  return (
    <>
      {/* Team Header */}
      <div className="section--spacing-x section--spacing-y grid grid-cols-1 gap-2 lg:grid-cols-3">
        <div className="mr-10">
          <h4 className="sub--title mb-3">The Team</h4>
          <h2 className="heading--one font-light text-secondary"></h2>
        </div>

        <div
          className="col-span-2"
          data-sal="slide-up"
          data-sal-delay="300"
          data-sal-duration="1200"
          data-sal-easing="easeInOutSine"
        >
          <p className="description--styles">
            Great developments are built on the synergy of real estate
            expertise, architectural excellence, and engineering precision. At
            BrownEarth Developments Ltd., we integrate these disciplines to
            deliver thoughtfully planned, meticulously designed, and expertly
            executed properties that create lasting value
          </p>
        </div>
      </div>

      {/* Leadership */}
      {grouped
        .filter((group) => group.category === "top-management")
        .map((group) => (
          <div key={group.category} className="section--spacing-x mb-16">
            <div className="mb-8 flex flex-col gap-2 border-b border-stone-300 pb-4 md:flex-row md:items-end md:justify-between">
              <h2 className="heading--one font-light text-secondary">
                {CATEGORY_META[group.category].label}
              </h2>
              <p className="sub--title max-w-sm md:text-right">
                {CATEGORY_META[group.category].note}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {group.members.map((member, index) => (
                <div
                  key={member.name}
                  className="group"
                  data-sal="slide-up"
                  data-sal-delay={150 * index}
                  data-sal-duration="1000"
                  data-sal-easing="easeInOutSine"
                >
                  <Monogram
                    name={member.name}
                    image={member.image}
                    size="text-5xl"
                  />
                  <div className="border-b-2 border-transparent bg-stone-200 p-5 duration-300 group-hover:border-secondary">
                    <h2 className="project--title text-xl">{member.name}</h2>
                    <h4 className="sub--title mt-1">{member.designation}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {/* Architecture & Engineering / Management & Sales */}
      {grouped
        .filter((group) => group.category !== "top-management")
        .map((group) => (
          <div
            key={group.category}
            className="mb-16 grid grid-cols-1 gap-2 bg-neutral-300 md:grid-cols-3"
          >
            <div className="section--spacing-x flex flex-col justify-center py-6">
              <h2 className="heading--one font-bold uppercase tracking-wider text-secondary">
                {CATEGORY_META[group.category].label}
              </h2>
              <p className="sub--title mt-3 max-w-xs">
                {CATEGORY_META[group.category].note}
              </p>
            </div>

            <div className="bg-stone-50 md:col-span-2">
              {group.members.map((member, index) => (
                <div
                  key={member.name}
                  className="flex items-center gap-5 border-b border-stone-300 px-6 py-5 duration-300 last:border-b-0 hover:bg-stone-200 md:px-10"
                  data-sal="slide-up"
                  data-sal-delay={100 * index}
                  data-sal-duration="800"
                  data-sal-easing="easeInOutSine"
                >
                  <div className="h-14 w-14 shrink-0">
                    <Monogram
                      name={member.name}
                      image={member.image}
                      size="text-lg"
                    />
                  </div>
                  <div>
                    <h2 className="project--title text-lg">{member.name}</h2>
                    <h4 className="sub--title mt-1">{member.designation}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </>
  );
};

export default Teams;
